import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Collapse, Menu, MenuItem, Stack } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { differenceInCalendarDays, format, parse } from 'date-fns';
import type {
    ElasticSearchFrameIdRequestPayload,
    ElasticSearchFrameIdMapDataResponse,
    ElasticSearchPerformanceStats,
    ElasticSearchSummaryResponse,
    PCAAnalyticsReportResponse,
    Campaign,
    CampaignStats,
    PCADisplayType,
} from 'c-sdk';
import { UserType } from 'c-sdk';
import { apiClient, useEntityData } from 'c-data';
import type { ReportConfig, ReportWrapperProps, ReportWrapperRefAPI } from 'c-reports/Types';
import { ReportDateTimeframe } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';
import { useCommonTranslation } from 'c-translation';
import { useTheme } from '@mui/styles';
import to from 'await-to-js';
import type { PCAUrlState } from 'c-main/Components/Campaign/CampaignReports/types';
import { PCAMetricUrlStateKeys } from 'c-main/Components/Campaign/CampaignReports/types';
import {
    useAPIClientRequest,
    useAuthenticatedUser,
    useDateUtils,
    useUrlState,
    useUrlStateValue,
} from 'c-hooks';
import { usePrevious } from 'react-hanger';
import { omit } from 'ramda';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import {
    allFieldValuesEs,
    getCadPlayoutDate,
    getCampaignReportEndDate,
    getCampaignReportStartDate,
    parseReportDate,
} from 'c-main/Lib';
import equal from 'fast-deep-equal';
import { Alert, Button } from 'c-components';
import { FilterList, KeyboardArrowDown } from '@mui/icons-material';
import { ReportFieldFilterES } from 'c-main/Components/Campaign/CampaignReports/ReportFieldFilter';
import { elasticSearchSummaryToOldSummary } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/SummaryTransformers';
import { supportsAverageAudience } from 'c-main/Lib';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useAtomValue } from 'jotai';
import { useDispatch } from 'react-redux';
import { NetworkRequestState } from 'c-data-layer';
import type { NetworkRequest } from 'c-data-layer';
import { callApi } from 'c-data-layer';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    atom_campaignSummaryWidgetConfigs,
    atom_useLinearReportingOverride,
} from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import SummaryWidgetsBody from './SummaryWidgetsBody';
import PCATotalsByMetric from './PCATotalsByMetric.main';
import type { SummaryWidgetConfig, SummaryWidgetMetric } from './summaryCustomizationConfig.main';
import {
    getDefaultSummaryWidgets,
    getSummaryWidgetConfigKey,
    normalizeSummaryWidgetConfig,
    SUMMARY_WIDGET_GROUP_BY_ES_FIELD_MAP,
} from './summaryCustomizationConfig.main';
import { getSummaryExportGroupColumns } from './campaignAnalyticsUtils';
import { useCampaignAnalyticsCompanionSummary } from './useCampaignAnalyticsCompanionSummary';
import { useCampaignAnalyticsDspState } from './useCampaignAnalyticsDspState';
import { useCampaignAnalyticsSummaryWidgets } from './useCampaignAnalyticsSummaryWidgets.main';
import { useCampaignAnalyticsWidgetData } from './useCampaignAnalyticsWidgetData';
import { useInsertionOrderTargets } from './useInsertionOrderTargets';

type Props = {
    id: number;
    filterExtras?: ReactNode[];
    /** Shown centred in the date/filter row (the summary switcher). */
    centerControls?: ReactNode;
};

type RawMapDataShape = ElasticSearchFrameIdMapDataResponse;

const urlDateFormat = 'yyyy-MM-dd';

/**
 * The last day a summary response has plays for. Days that report no plays are
 * ignored, so the campaign is judged up to the last day it actually ran.
 *
 * Always read this from the same response the impacts are taken from. Which days
 * a summary covers depends on how it was asked: given explicit dates the API
 * returns everything in that window, but given none it falls back to settled days
 * only. Judging one response's impacts against another response's day count
 * mixes the two and skews the percentage.
 */
const latestDailyStatsDate = (
    dailyStats: Record<string, ElasticSearchPerformanceStats> | null | undefined,
): Date | null => {
    if (!dailyStats) return null;

    return Object.entries(dailyStats).reduce<Date | null>((latest, [dateValue, stats]) => {
        if (!(Number(stats?.sum_of_plays) > 0)) return latest;

        const parsed = parse(dateValue, urlDateFormat, new Date());
        if (Number.isNaN(parsed.getTime())) return latest;
        return !latest || parsed > latest ? parsed : latest;
    }, null);
};

const NO_AUDIENCE_VALUE = '__NO_AUDIENCE__';
const DEFAULT_SCHEDULED = ['IN', 'OUT', 'UNBOOKED'];
const DSP_AUDIENCE_FILTER_KEY = 'audience_display_name';
const DSP_NO_AUDIENCE_VALUE = 'nothing';
const DSP_AUDIENCE_MODE_URL_KEY = 'dspAudienceMode';
const DSP_AUDIENCE_MODE_AVERAGES = 'averages';
const CampaignAnalyticsES: React.FC<Props> = ({ id, filterExtras, centerControls }) => {
    const theme = useTheme();
    const t = useCommonTranslation();
    const dispatch = useDispatch();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaignSummaryWidgetConfigs = useAtomValue(atom_campaignSummaryWidgetConfigs);
    const campaign = getById({ id });
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [summaryLoadingState, setSummaryLoadingState] = useState<NetworkRequestState>(
        NetworkRequestState.Idle,
    );
    const [summaryTotalsStale, setSummaryTotalsStale] = useState(false);
    const [overallUnfilteredCampaignStats, setOverallUnfilteredCampaignStats] =
        useState<CampaignStats | null>(null);
    const [overallUnfilteredSummaryDataDaysCount, setOverallUnfilteredSummaryDataDaysCount] =
        useState(0);
    const overallSummaryRequestRef = useRef<AbortController | null>(null);
    const latestOverallSummaryRequestKeyRef = useRef<string | null>(null);
    const open = Boolean(anchorEl);

    const campaignHasDspReporting = useMemo(
        () => campaign.type !== 'linear' && campaign.type !== 'default',
        [campaign.type],
    );
    const useLinearReportingOverride = useAtomValue(atom_useLinearReportingOverride);
    const isDsp = campaignHasDspReporting && !useLinearReportingOverride;

    const { formatDateStringIgnoreTime, dayMonthYearFormat } = useDateUtils();
    const { push, replaceMany } = useUrlState();
    const dspAudienceMode =
        useUrlStateValue<string | null>(DSP_AUDIENCE_MODE_URL_KEY, null) ?? null;
    const isAverageAudienceSupported = useMemo(
        () => supportsAverageAudience({ type: campaign.type }),
        [campaign.type],
    );
    const isAverageAudienceMode = useMemo(
        () => isDsp && isAverageAudienceSupported && dspAudienceMode === DSP_AUDIENCE_MODE_AVERAGES,
        [dspAudienceMode, isAverageAudienceSupported, isDsp],
    );

    useEffect(() => {
        if ((isDsp && isAverageAudienceSupported) || dspAudienceMode == null) return;
        replaceMany({ [DSP_AUDIENCE_MODE_URL_KEY]: null });
    }, [dspAudienceMode, isAverageAudienceSupported, isDsp, replaceMany]);

    const {
        environmentMode,
        hasPCAData,
        showAdvancedFilters,
        advancedFilters,
        advancedFiltersCount,
        onAdvancedFiltersUpdated,
        onResetAdvancedFilters,
        countFields,
        source,
        urlDateValue,
    } = useCampaignWrapperState(id);
    const hasSelectedDspAudienceFilter = useMemo(
        () =>
            Array.isArray(advancedFilters?.[DSP_AUDIENCE_FILTER_KEY]) &&
            advancedFilters[DSP_AUDIENCE_FILTER_KEY].some(v => v != null && v !== ''),
        [advancedFilters],
    );
    const hasSelectedAuditedDspAudienceFilter = useMemo(
        () =>
            Array.isArray(advancedFilters?.[DSP_AUDIENCE_FILTER_KEY]) &&
            advancedFilters[DSP_AUDIENCE_FILTER_KEY].some(
                v => v != null && v !== '' && v !== DSP_NO_AUDIENCE_VALUE,
            ),
        [advancedFilters],
    );
    const prevHasSelectedDspAudienceFilter = usePrevious(hasSelectedDspAudienceFilter);

    useEffect(() => {
        if (!isAverageAudienceMode || !hasSelectedAuditedDspAudienceFilter) return;
        if (prevHasSelectedDspAudienceFilter !== false) return;
        replaceMany({ [DSP_AUDIENCE_MODE_URL_KEY]: null });
    }, [
        hasSelectedAuditedDspAudienceFilter,
        isAverageAudienceMode,
        prevHasSelectedDspAudienceFilter,
        replaceMany,
    ]);

    const isGeopathOnly = useMemo(() => {
        if (!isDsp) return false;
        const selected = (advancedFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? []).filter(
            v => v && v !== '',
        );
        if (selected.length !== 1) return false;
        return selected[0].toLowerCase().includes('geopath');
    }, [advancedFilters, isDsp]);
    const isRouteOnly = useMemo(() => {
        if (!isDsp) return false;
        const selected = (advancedFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? []).filter(
            v => v && v !== '',
        );
        if (selected.length !== 1) return false;
        return selected[0].toLowerCase().includes('route');
    }, [advancedFilters, isDsp]);

    const urlType = useUrlStateValue<'digital' | 'classic' | null>('type', null);
    const availableTypes = useMemo<ReadonlyArray<PCADisplayType>>(
        () => (environmentMode ? campaign?.pca_display_types?.[environmentMode] ?? [] : []),
        [campaign?.pca_display_types, environmentMode],
    );
    const hasKnownDisplayTypesForEnvironment = useMemo(
        () =>
            Boolean(
                environmentMode &&
                    campaign?.pca_display_types &&
                    Object.prototype.hasOwnProperty.call(
                        campaign.pca_display_types,
                        environmentMode,
                    ),
            ),
        [campaign?.pca_display_types, environmentMode],
    );
    const hasDigitalRaw = availableTypes.includes('Display');
    const hasClassic = availableTypes.includes('Classic');
    const hasDigital =
        hasDigitalRaw || (hasKnownDisplayTypesForEnvironment && availableTypes.length === 0);
    const resolvedType = useMemo<'digital' | 'classic' | null>(() => {
        if (urlType === 'digital' && hasDigital) return 'digital';
        if (urlType === 'classic' && hasClassic) return 'classic';
        if (hasDigital) return 'digital';
        if (hasClassic) return 'classic';

        return null;
    }, [hasClassic, hasDigital, urlType]);
    const [pcaData, setPcaData] = useState<ElasticSearchSummaryResponse>(null);
    const allValuesByField = useMemo(
        () => (pcaData ? allFieldValuesEs(pcaData?.filters) : null),
        [pcaData],
    );
    const dspAverageImpactsLabel = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.dspAverageImpacts',
    );
    const impactsWord = useCommonTranslation('CommonWords.Impacts');
    const {
        dspAudienceFilterValues,
        selectedAudienceLabelForCharts,
        fieldFilters,
        impactAnalysisFieldFilters,
        nonAuditedTotalFieldFilters,
        isNonAuditedAudienceOnly,
        hideImpressionAnalysis,
        audienceModeLabel,
        allValuesByFieldForUi,
        selectedFiltersForUi,
        audienceVersionOptions,
        audienceButtonLabel,
        impressionAnalysisVarianceContextLabel,
        impactAnalysisAudienceLabel,
    } = useCampaignAnalyticsDspState({
        advancedFilters,
        allValuesByField,
        resolvedType,
        isDsp,
        isAverageAudienceMode,
        dspAudienceFilterKey: DSP_AUDIENCE_FILTER_KEY,
        dspNoAudienceValue: DSP_NO_AUDIENCE_VALUE,
        noAudienceUiValue: NO_AUDIENCE_VALUE,
        dspAverageImpactsLabel,
        impactsWord,
    });

    const currentFieldFiltersKey = useMemo(
        () => JSON.stringify(fieldFilters ?? {}),
        [fieldFilters],
    );
    const currentFieldFiltersKeyRef = useRef(currentFieldFiltersKey);
    currentFieldFiltersKeyRef.current = currentFieldFiltersKey;
    const [loadedFieldFiltersKey, setLoadedFieldFiltersKey] = useState<string | null>(null);
    const summaryFiltersStale = loadedFieldFiltersKey !== currentFieldFiltersKey;

    const stripScheduledForDsp = useCallback(
        (filters?: Record<string, string[]>) =>
            (isDsp ? (omit(['scheduled'], filters ?? {}) as Record<string, string[]>) : filters) ??
            {},
        [isDsp],
    );
    const onAdvancedFiltersUpdatedScoped = useCallback(
        (filters: Record<string, string[]>, counts: any) =>
            onAdvancedFiltersUpdated(stripScheduledForDsp(filters), counts),
        [onAdvancedFiltersUpdated, stripScheduledForDsp],
    );

    useEffect(() => {
        if (isDsp) return;
        if (advancedFilters?.scheduled?.length) return;

        onAdvancedFiltersUpdated(
            {
                ...(advancedFilters ?? {}),
                scheduled: DEFAULT_SCHEDULED as any,
            },
            countFields,
        );
    }, [advancedFilters, countFields, isDsp, onAdvancedFiltersUpdated]);

    const campaignStart = useMemo(
        () => getCampaignReportStartDate(campaign, environmentMode) ?? new Date(),
        [campaign, environmentMode],
    );

    const campaignEnd = useMemo(
        () => getCampaignReportEndDate(campaign, environmentMode) ?? new Date(),
        [campaign, environmentMode],
    );

    const campaignTrueStart = useMemo(
        () => parseReportDate(campaign?.date_start) ?? new Date(),
        [campaign?.date_start],
    );
    const campaignTrueEnd = useMemo(
        () => parseReportDate(campaign?.date_end) ?? new Date(),
        [campaign?.date_end],
    );

    const maxCadPlayoutStart = getCadPlayoutDate(
        campaign?.max_cad_entry_playout_start_datetime,
        environmentMode,
    );
    /**
     * The last day the summary on screen has data for — taken from that same
     * response, so the impacts and the days they are judged against always come
     * from one query. It arrives with the stats, so the first paint is right and
     * nothing replaces it a moment later.
     *
     * `max_cad_entry_playout_start_datetime` is only a fallback for before the
     * summary lands. It counts every playout the campaign holds, settled or not,
     * so it can read a day or two ahead of what any given summary covers.
     */
    const summaryAsOfDate = useMemo(
        () => latestDailyStatsDate((pcaData as any)?.data?.daily_stats),
        [pcaData],
    );
    const campaignAsOfDate = useMemo(() => {
        const dailyStatsDate =
            isDsp && summaryAsOfDate && !Number.isNaN(summaryAsOfDate.getTime())
                ? summaryAsOfDate
                : null;
        const cadDate = parseReportDate(maxCadPlayoutStart);
        let base: Date;
        if (dailyStatsDate) base = dailyStatsDate;
        else if (cadDate && !Number.isNaN(cadDate.getTime())) base = cadDate;
        else base = new Date();
        return base > campaignTrueEnd ? campaignTrueEnd : base;
    }, [isDsp, summaryAsOfDate, maxCadPlayoutStart, campaignTrueEnd]);

    const { chosenUserType } = useAuthenticatedUser();
    const isAdmin = chosenUserType === UserType.Admin;
    const maxCadDate = useMemo(() => parseReportDate(maxCadPlayoutStart), [maxCadPlayoutStart]);
    const filterMaxDate = useMemo(
        () =>
            isAdmin
                ? [campaignTrueEnd, maxCadDate].reduce(
                      (acc, d) => (d && d > acc ? d : acc),
                      campaignEnd,
                  )
                : campaignEnd,
        [isAdmin, campaignEnd, campaignTrueEnd, maxCadDate],
    );

    const initialStartDate = useMemo(
        () =>
            urlDateValue?.startDate
                ? parse(urlDateValue.startDate, urlDateFormat, new Date())
                : campaignStart,
        [urlDateValue?.startDate, campaignStart],
    );

    const initialEndDate = useMemo(
        () =>
            urlDateValue?.endDate
                ? parse(urlDateValue.endDate, urlDateFormat, new Date())
                : campaignEnd,
        [urlDateValue?.endDate, campaignEnd],
    );

    const toDateAsOfDate = useMemo(
        () => (initialEndDate > campaignAsOfDate ? campaignAsOfDate : initialEndDate),
        [initialEndDate, campaignAsOfDate],
    );
    const hasSummaryDateFilter = useMemo(
        () => urlDateValue?.timeframe != null && urlDateValue.timeframe !== ReportDateTimeframe.All,
        [urlDateValue?.timeframe],
    );

    // UL-8133 -- a vistar_us2 campaign paces against its insertion order targets, not evenly.
    const summaryDateFilter = useMemo(
        () => (hasSummaryDateFilter ? { start: initialStartDate, end: initialEndDate } : null),
        [hasSummaryDateFilter, initialEndDate, initialStartDate],
    );
    const ioTargets = useInsertionOrderTargets({
        campaign,
        isDsp,
        advancedFilters,
        dateFilter: summaryDateFilter,
        environment: environmentMode,
        source,
        fieldFilters,
        dailyStats: (pcaData as any)?.data?.daily_stats,
    });

    const reportWrapperRef = useRef<ReportWrapperRefAPI>();
    const prevCountFields = usePrevious(countFields);
    const prevAdvancedFilters = usePrevious(advancedFilters);

    const [timeframe, setTimeframe] = useState<ReportDateTimeframe | null>(
        urlDateValue?.timeframe ?? ReportDateTimeframe.All,
    );

    const genKey = useCallback(
        (date: Date, date2: Date) =>
            `${format(date, urlDateFormat)}-${format(date2, urlDateFormat)}`,
        [],
    );

    const [currentFilteredMapDataKey, setCurrentFilteredMapDataKey] = useState(
        genKey(campaignStart, campaignEnd),
    );

    const [filteredMapData, setFilteredMapData] = useState<Record<string, RawMapDataShape>>({});
    const [overallUnfilteredMapData, setOverallUnfilteredMapData] = useState<
        Record<string, RawMapDataShape>
    >({});
    const overallMapFetchKeyRef = useRef<string | null>(null);

    // const scrollDownOnce = useRef(false);
    // const prevData = usePrevious(data);
    useLayoutEffect(() => {
        // just to stop it fetching data on mount
        if (prevCountFields != undefined && prevAdvancedFilters != undefined) {
            setSummaryTotalsStale(true);
            setImpactAnalysisLoading(isDsp);
            setFilteredMapData(curr => omit([currentFilteredMapDataKey], curr));
            reportWrapperRef?.current?.refreshData?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countFields, advancedFilters]);

    // BP - Not sure why this was ever in here - leaving in case it bites me later
    // useEffect(() => {
    //     if (prevData == null && data != null && !scrollDownOnce.current) {
    //         scrollDownOnce.current = true;
    //         scrollToTopReportingTab();
    //     }
    // }, [data, prevData]);

    const data = useMemo<PCAAnalyticsReportResponse | null>(
        () => elasticSearchSummaryToOldSummary(pcaData),
        [pcaData],
    );

    const { start: StartGetFilteredMapData } = useAPIClientRequest(
        apiClient.ReportsElasticSearch.frameIdMapData,
    );
    const { start: StartGetOverallMapData } = useAPIClientRequest(
        apiClient.ReportsElasticSearch.frameIdMapData,
    );
    const actualLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.impacts.actualLabel',
    );
    const actualDeliveredLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.totalDeliveredImpacts',
    );
    const deliveredNonAuditedLabel = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.deliveredNonAuditedImpressions',
    );
    const shareOfTotalLabel = isNonAuditedAudienceOnly
        ? deliveredNonAuditedLabel
        : actualDeliveredLabelImpacts;
    const percentLabelDecimals = isDsp && !isGeopathOnly && !isAverageAudienceMode ? 2 : 0;
    const actualLabelPlays = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.plays.actualLabel',
    );

    const percentageLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.impacts.percentageLabel',
    );
    const percentageLabelPlays = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.plays.percentageLabel',
    );

    const performanceLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.impacts.performanceLabel',
    );

    const expectedLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.impacts.expectedLabel',
    );
    const expectedLabelPlays = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.plays.expectedLabel',
    );

    const missingLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.impacts.missingLabel',
    );
    const missingLabelPlays = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.plays.missingLabel',
    );

    const dateLabelLabel = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.dateLabel',
    );
    const inScheduleLabel = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.inScheduleLabel',
    );
    const outScheduleLabel = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.outScheduleLabel',
    );
    const unbookedLabel = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.unbookedLabel',
    );

    const auditedLabelImpacts = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Reports.auditedImpacts',
    );
    const varianceLabel = useCommonTranslation('Modules.Main.Campaigns.Overview.dsp.var2');
    const metricTitles = useMemo<Record<SummaryWidgetMetric, string>>(
        () => ({
            impacts: t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle'),
            plays: t(
                resolvedType === 'classic'
                    ? 'Modules.Main.Campaigns.Overview.Analytics.displayDaysTitle'
                    : 'Modules.Main.Campaigns.Overview.Analytics.playsTitle',
            ),
        }),
        [resolvedType, t],
    );
    const summaryTitleBy = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.summaryTitleBy',
    );
    const summaryTitleJoin = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.summaryTitleJoin',
    );
    const summaryTitleRunningTotal = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.summaryTitleRunningTotal',
    );
    const summaryTitleHeatmap = useCommonTranslation(
        'Modules.Main.Campaigns.Overview.Analytics.summaryTitleHeatmap',
    );

    const latestSummaryWindow = useRef<{
        start: Date;
        end: Date;
        timeframe: ReportDateTimeframe;
    } | null>(null);
    const expectedLabelForImpactsCharts = useMemo(() => {
        if (!isDsp) return expectedLabelImpacts;
        if (isAverageAudienceMode) return dspAverageImpactsLabel;
        if (selectedAudienceLabelForCharts)
            return `${selectedAudienceLabelForCharts} ${impactsWord}`;
        return auditedLabelImpacts;
    }, [
        auditedLabelImpacts,
        dspAverageImpactsLabel,
        expectedLabelImpacts,
        impactsWord,
        isAverageAudienceMode,
        isDsp,
        selectedAudienceLabelForCharts,
    ]);
    const pickAverageImpactsValue = useCallback(
        (stats: any) =>
            Number(
                stats?.dsp_average_impressions ??
                    stats?.dsp_average_impacts ??
                    stats?.average_impressions ??
                    0,
            ),
        [],
    );

    const buildGroupByFieldDateRange = useCallback(
        (start: Date, end: Date, tf: ReportDateTimeframe) => ({
            startDate:
                tf === ReportDateTimeframe.All
                    ? undefined
                    : `${format(start, 'yyyy-MM-dd')} 00:00:00`,
            endDate:
                tf === ReportDateTimeframe.All
                    ? undefined
                    : `${format(end, 'yyyy-MM-dd')} 23:59:59`,
        }),
        [],
    );

    const getMetricChartLabels = useCallback(
        (metric: SummaryWidgetMetric) => {
            if (metric === 'plays') {
                return {
                    actualLabel: actualLabelPlays,
                    expectedLabel: expectedLabelPlays,
                    percentageLabel: percentageLabelPlays,
                    missingLabel: missingLabelPlays,
                };
            }

            return {
                actualLabel: actualLabelImpacts,
                actualLabelOverride: isDsp ? actualDeliveredLabelImpacts : undefined,
                expectedLabel: expectedLabelForImpactsCharts,
                percentageLabel: percentageLabelImpacts,
                missingLabel: missingLabelImpacts,
            };
        },
        [
            actualDeliveredLabelImpacts,
            actualLabelImpacts,
            actualLabelPlays,
            expectedLabelForImpactsCharts,
            expectedLabelPlays,
            isDsp,
            missingLabelImpacts,
            missingLabelPlays,
            percentageLabelImpacts,
            percentageLabelPlays,
        ],
    );

    const formatSignedWhole = useCallback(
        (value: number) => `${value > 0 ? '+' : ''}${Math.round(value)}`,
        [],
    );

    const buildSummaryExportData = useCallback(
        (rows: any[] | null | undefined, metric: SummaryWidgetMetric, xAxisLabels: string[]) => {
            if (!Array.isArray(rows)) return undefined;

            const labels = getMetricChartLabels(metric);
            const hasComparison =
                isDsp &&
                metric === 'impacts' &&
                (isAverageAudienceMode || selectedAudienceLabelForCharts != null);

            return rows.map(row => {
                const groupColumns = getSummaryExportGroupColumns(row, xAxisLabels, dateLabelLabel);
                const total = Number(row?.[labels.actualLabel] ?? 0);
                const comparison = Number(row?.[labels.expectedLabel] ?? 0);

                if (!hasComparison) {
                    return {
                        ...groupColumns,
                        [labels.actualLabel]: total,
                    };
                }

                const variance = comparison - total;
                const percentage = total > 0 ? (comparison / total) * 100 : 0;

                return {
                    ...groupColumns,
                    [labels.expectedLabel]: comparison,
                    [labels.actualLabel]: total,
                    [varianceLabel]: formatSignedWhole(variance),
                    [labels.percentageLabel]: percentage,
                };
            });
        },
        [
            dateLabelLabel,
            formatSignedWhole,
            getMetricChartLabels,
            isAverageAudienceMode,
            isDsp,
            selectedAudienceLabelForCharts,
            varianceLabel,
        ],
    );

    const buildMetricBarRow = useCallback(
        (label: string, stats: any, metric: SummaryWidgetMetric) => {
            const labels = getMetricChartLabels(metric);
            const actual =
                metric === 'plays' ? Number(stats?.sum_of_plays ?? 0) : Number(stats?.impacts ?? 0);
            const expected =
                metric === 'plays'
                    ? Number(stats?.expected_plays_in_schedule ?? 0)
                    : Number(
                          isDsp && isAverageAudienceMode
                              ? pickAverageImpactsValue(stats)
                              : isDsp && (isGeopathOnly || selectedAudienceLabelForCharts != null)
                              ? stats?.geopath_impressions ?? stats?.geopath_audience ?? 0
                              : stats?.expected_impacts_in_schedule ?? 0,
                      );
            const inSchedule =
                metric === 'plays'
                    ? Number(stats?.plays_received_in_schedule ?? 0)
                    : Number(stats?.impacts_received_in_schedule ?? 0);
            const outSchedule =
                metric === 'plays'
                    ? Number(stats?.plays_received_out_of_schedule ?? 0)
                    : Number(stats?.impacts_received_out_of_schedule ?? 0);
            const unbooked =
                metric === 'plays'
                    ? Number(stats?.plays_received_unbooked ?? 0)
                    : Number(stats?.impacts_received_unbooked ?? 0);

            return {
                [dateLabelLabel]: label,
                [labels.actualLabel]: actual,
                [labels.expectedLabel]: expected,
                [labels.percentageLabel]: expected > 0 ? (actual / expected) * 100 : 0,
                [labels.missingLabel]: actual <= expected ? expected - actual : 0,
                [inScheduleLabel]: inSchedule,
                [outScheduleLabel]: outSchedule,
                [unbookedLabel]: unbooked,
            };
        },
        [
            dateLabelLabel,
            getMetricChartLabels,
            inScheduleLabel,
            isDsp,
            isAverageAudienceMode,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
            outScheduleLabel,
            pickAverageImpactsValue,
            unbookedLabel,
        ],
    );

    const mapFetchFilters = useRef<ElasticSearchFrameIdRequestPayload>(null);

    const fetchFilteredMapData = useCallback(
        async (start: Date, end: Date, tf: ReportDateTimeframe) => {
            const mapKey = genKey(start, end);
            setCurrentFilteredMapDataKey(mapKey);

            const config: ElasticSearchFrameIdRequestPayload = {
                campaignId: id,
                startDate:
                    tf === ReportDateTimeframe.All ? undefined : format(start, urlDateFormat),
                endDate: tf === ReportDateTimeframe.All ? undefined : format(end, urlDateFormat),
                environment: environmentMode,
                fieldFilters,
                source,
            };

            if (filteredMapData[mapKey] == null && !equal(mapFetchFilters.current, config)) {
                mapFetchFilters.current = { ...config };
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const [err, reportData] = await to(StartGetFilteredMapData(config));

                if (!err && reportData?.data?.data != null) {
                    setFilteredMapData(val => ({
                        ...val,
                        [mapKey]: reportData?.data,
                    }));
                }
            }
        },
        [
            StartGetFilteredMapData,
            environmentMode,
            fieldFilters,
            filteredMapData,
            genKey,
            id,
            source,
        ],
    );

    const fetchOverallUnfilteredMapData = useCallback(
        async (start: Date, end: Date, tf: ReportDateTimeframe) => {
            if (!isDsp) return;
            const mapKey = genKey(start, end);
            const config: ElasticSearchFrameIdRequestPayload = {
                campaignId: id,
                startDate:
                    tf === ReportDateTimeframe.All ? undefined : format(start, urlDateFormat),
                endDate: tf === ReportDateTimeframe.All ? undefined : format(end, urlDateFormat),
                environment: environmentMode,
                fieldFilters: {},
                source,
            };
            const dedupeKey = JSON.stringify(config);
            if (
                overallUnfilteredMapData[mapKey] != null &&
                overallMapFetchKeyRef.current === dedupeKey
            )
                return;
            overallMapFetchKeyRef.current = dedupeKey;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const [err, reportData] = await to(StartGetOverallMapData(config));
            if (!err && reportData?.data?.data != null) {
                setOverallUnfilteredMapData(val => ({ ...val, [mapKey]: reportData?.data }));
            }
        },
        [
            StartGetOverallMapData,
            environmentMode,
            genKey,
            id,
            isDsp,
            overallUnfilteredMapData,
            source,
        ],
    );

    const fetchData: ReportConfig<PCAAnalyticsReportResponse>['fetchData'] = useCallback(
        (start, end, tf) =>
            apiClient.ReportsElasticSearch.summary({
                campaignId: id,
                startDate:
                    tf === ReportDateTimeframe.All ? undefined : format(start, urlDateFormat),
                endDate: tf === ReportDateTimeframe.All ? undefined : format(end, urlDateFormat),
                environment: environmentMode,
                fieldFilters,
                showStats: true,
                source,
            }),
        [environmentMode, fieldFilters, id, source],
    );

    const visibleWidgetConfigs = useMemo(
        () =>
            (
                campaignSummaryWidgetConfigs[getSummaryWidgetConfigKey(id, isDsp)] ??
                getDefaultSummaryWidgets(isDsp)
            ).map(normalizeSummaryWidgetConfig),
        [campaignSummaryWidgetConfigs, id, isDsp],
    );

    const dateSettings = useMemo<ReportWrapperProps<PCAAnalyticsReportResponse>['dateSettings']>(
        () => ({
            minDate: campaignStart,
            maxDate: filterMaxDate,
            availableTimeframes: [
                ReportDateTimeframe.All,
                ReportDateTimeframe.Range,
                ReportDateTimeframe.Monthly,
                ReportDateTimeframe.Weekly,
                ReportDateTimeframe.Daily,
            ],
            timeframe: urlDateValue?.timeframe ?? ReportDateTimeframe.All,
            onDatesUpdated: (tf, start, end) => {
                push(PCAMetricUrlStateKeys.date, {
                    timeframe: tf,
                    startDate:
                        start && tf !== ReportDateTimeframe.All
                            ? format(start, urlDateFormat)
                            : undefined,
                    endDate:
                        end && tf !== ReportDateTimeframe.All
                            ? format(end, urlDateFormat)
                            : undefined,
                } as PCAUrlState['date']);
            },
        }),
        [campaignStart, filterMaxDate, push, urlDateValue?.timeframe],
    );

    const geopathDailyLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.daily_stats ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([date, row]: any) => [date, row?.geopath_audience]),
        );
    }, [pcaData]);

    const geopathMediaOwnerLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.display_owner ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, row?.geopath_audience]),
        );
    }, [pcaData]);

    const geopathRegionLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.display_tv_region ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, row?.geopath_audience]),
        );
    }, [pcaData]);

    const geopathDowLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.day_of_week ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, row?.geopath_audience]),
        );
    }, [pcaData]);
    const geopathHourLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.hour ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, row?.geopath_audience]),
        );
    }, [pcaData]);

    const averageDailyLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.daily_stats ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([date, row]: any) => [date, pickAverageImpactsValue(row)]),
        );
    }, [pcaData, pickAverageImpactsValue]);

    const averageMediaOwnerLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.display_owner ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, pickAverageImpactsValue(row)]),
        );
    }, [pcaData, pickAverageImpactsValue]);

    const averageRegionLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.display_tv_region ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, pickAverageImpactsValue(row)]),
        );
    }, [pcaData, pickAverageImpactsValue]);

    const averageDowLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.day_of_week ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, pickAverageImpactsValue(row)]),
        );
    }, [pcaData, pickAverageImpactsValue]);
    const averageHourLookup = useMemo<Record<string, number>>(() => {
        const stats = (pcaData as any)?.data?.hour ?? {};
        return Object.fromEntries(
            Object.entries(stats).map(([k, row]: any) => [k, pickAverageImpactsValue(row)]),
        );
    }, [pcaData, pickAverageImpactsValue]);
    const {
        auditedPcaData,
        nonAuditedTotalPcaData,
        impactAnalysisLoading,
        setImpactAnalysisLoading,
        loadCompanionSummaries,
    } = useCampaignAnalyticsCompanionSummary({
        id,
        isDsp,
        hideImpressionAnalysis,
        isNonAuditedAudienceOnly,
        environmentMode,
        impactAnalysisFieldFilters,
        nonAuditedTotalFieldFilters,
        source,
        dispatch,
        urlDateFormat,
    });

    useEffect(() => {
        const summaryWindow = latestSummaryWindow.current;
        if (!isDsp || !summaryWindow) return;
        loadCompanionSummaries(summaryWindow);
    }, [isDsp, loadCompanionSummaries]);

    const hasUserActiveFilters = useMemo(() => {
        if (!advancedFilters) return false;

        return Object.entries(advancedFilters).some(([key, value]) => {
            if (!value || value.length === 0) return false;

            if (key === 'display_type') return false;
            if (key === 'scheduled') return false;

            return true;
        });
    }, [advancedFilters]);
    const isDateOnlyImpactLineWidget = useCallback(
        (widget: SummaryWidgetConfig) =>
            widget.kind === 'line' &&
            widget.metric === 'impacts' &&
            widget.groupBy.length === 1 &&
            widget.groupBy[0] === 'date',
        [],
    );
    // UL-8133 -- a vistar_us2 campaign keeps its expected line under an insertion order filter.
    const expectedSurvivesFilters = ioTargets.isTargetCampaign
        ? ioTargets.showTargets
        : !hasUserActiveFilters;
    const showDefaultDspExpectedLine = useCallback(
        (widget: SummaryWidgetConfig) =>
            isDsp &&
            expectedSurvivesFilters &&
            !isAverageAudienceMode &&
            dspAudienceFilterValues.length === 0 &&
            isDateOnlyImpactLineWidget(widget),
        [
            dspAudienceFilterValues.length,
            expectedSurvivesFilters,
            isAverageAudienceMode,
            isDateOnlyImpactLineWidget,
            isDsp,
        ],
    );
    const showDefaultDspExpectedBar = useCallback(
        (widget: SummaryWidgetConfig) =>
            isDsp &&
            expectedSurvivesFilters &&
            !isAverageAudienceMode &&
            dspAudienceFilterValues.length === 0 &&
            widget.metric === 'impacts' &&
            widget.kind === 'bar' &&
            widget.groupBy.length === 1 &&
            widget.groupBy[0] === 'date',
        [dspAudienceFilterValues.length, expectedSurvivesFilters, isAverageAudienceMode, isDsp],
    );
    const showDefaultDspTargetPercent = useCallback(
        (widget: SummaryWidgetConfig) =>
            isDsp &&
            !isAverageAudienceMode &&
            !isGeopathOnly &&
            (dspAudienceFilterValues.length === 0 || isNonAuditedAudienceOnly) &&
            widget.metric === 'impacts' &&
            widget.kind === 'bar' &&
            !(widget.groupBy.length === 1 && widget.groupBy[0] === 'date'),
        [
            dspAudienceFilterValues.length,
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            isNonAuditedAudienceOnly,
        ],
    );

    const applyComparisonExpected = useCallback(
        (rows: any[] | null, lookup: Record<string, number>) => {
            if (
                !rows ||
                !isDsp ||
                (!isGeopathOnly && !isAverageAudienceMode && selectedAudienceLabelForCharts == null)
            )
                return rows;

            return rows.map(r => {
                const key = r?.[dateLabelLabel];
                const audited = lookup?.[key];
                if (audited == null) return r;

                return { ...r, [expectedLabelForImpactsCharts]: audited };
            });
        },
        [
            dateLabelLabel,
            expectedLabelForImpactsCharts,
            isAverageAudienceMode,
            isDsp,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
        ],
    );
    const { adjustedCampaignTarget, fetchExtraWidgetData, getWidgetData } =
        useCampaignAnalyticsWidgetData({
            campaignExpectedImpacts: campaign.expected_impacts,
            campaignStart: campaignTrueStart,
            campaignEnd: campaignTrueEnd,
            campaignAsOfDate,
            windowStart: hasSummaryDateFilter ? initialStartDate : campaignTrueStart,
            isDsp,
            isAverageAudienceMode,
            isGeopathOnly,
            selectedAudienceLabelForCharts,
            hasUserActiveFilters,
            dspAudienceFilterCount: dspAudienceFilterValues.length,
            data,
            dateLabelLabel,
            actualLabelImpacts,
            actualDeliveredLabelImpacts,
            shareOfTotalLabel,
            percentageLabelImpacts,
            expectedLabelForImpactsCharts,
            missingLabelImpacts,
            actualLabelPlays,
            percentageLabelPlays,
            expectedLabelPlays,
            missingLabelPlays,
            performanceLabelImpacts,
            inScheduleLabel,
            outScheduleLabel,
            unbookedLabel,
            dayMonthYearFormat,
            averageDailyLookup,
            geopathDailyLookup,
            averageDowLookup,
            geopathDowLookup,
            averageHourLookup,
            geopathHourLookup,
            averageRegionLookup,
            geopathRegionLookup,
            averageMediaOwnerLookup,
            geopathMediaOwnerLookup,
            applyComparisonExpected,
            buildGroupByFieldDateRange,
            buildMetricBarRow,
            fieldFilters,
            environmentMode,
            source,
            id,
            dispatch,
            showDefaultDspExpectedLine,
            showDefaultDspExpectedBar,
            showDefaultDspTargetPercent,
            summaryWidgetGroupByEsFieldMap: SUMMARY_WIDGET_GROUP_BY_ES_FIELD_MAP,
            filteredMapData,
            currentFilteredMapDataKey,
            targetSeries: ioTargets.targetSeries,
        });
    const visibleReportDefinitions = useCampaignAnalyticsSummaryWidgets({
        id,
        source,
        environmentMode,
        fieldFilters,
        isDsp,
        isAverageAudienceMode,
        isGeopathOnly,
        selectedAudienceLabelForCharts,
        showDefaultDspExpectedLine,
        showDefaultDspExpectedBar,
        showDefaultDspTargetPercent,
        actualDeliveredLabelImpacts,
        shareOfTotalLabel,
        percentLabelDecimals,
        actualLabelImpacts,
        actualLabelPlays,
        expectedLabelForImpactsCharts,
        expectedLabelImpacts,
        expectedLabelPlays,
        percentageLabelImpacts,
        percentageLabelPlays,
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        timeframe,
        countFields,
        metricTitles,
        summaryTitleBy,
        summaryTitleHeatmap,
        summaryTitleJoin,
        summaryTitleRunningTotal,
        dateLabelLabel,
        visibleWidgetConfigs,
        initialStartDate,
        initialEndDate,
        summaryLoadingState,
        latestSummaryWindow,
        getWidgetData,
        getMetricChartLabels,
        fetchExtraWidgetData,
        buildSummaryExportData,
        targetSeriesKey: ioTargets.seriesKey,
    });

    const renderedSummaryWidgets = useMemo(
        () => (
            <Stack gap={3}>
                {visibleReportDefinitions.map((definition, i) => {
                    const { RenderReport, ...reportProps } = definition.report as any;

                    return (
                        <Card
                            key={definition.id}
                            sx={(definition.report as any)?.props?.children?.props?.chartProps?.sx}
                        >
                            <CardHeader
                                title={`${definition.title ?? ''}${
                                    definition.isHeatmap
                                        ? ''
                                        : audienceModeLabel
                                        ? ` ${audienceModeLabel}`
                                        : ''
                                }`}
                            />
                            <CardContent>
                                {RenderReport ? <RenderReport {...reportProps} /> : null}
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        ),
        [audienceModeLabel, visibleReportDefinitions],
    );

    const advancedFilter = useMemo(
        () =>
            allValuesByFieldForUi ? (
                <ReportFieldFilterES
                    allValuesByField={allValuesByFieldForUi}
                    selectedFields={selectedFiltersForUi}
                    onSearch={onAdvancedFiltersUpdatedScoped}
                    onReset={onResetAdvancedFilters}
                />
            ) : null,
        [
            allValuesByFieldForUi,
            selectedFiltersForUi,
            onAdvancedFiltersUpdatedScoped,
            onResetAdvancedFilters,
        ],
    );

    const campaignValues = useMemo(
        () => ({
            show:
                (campaign.delivered_value !== null || campaign.bought_value !== null) &&
                Boolean(campaign.show_campaign_values_box),
            delivered: campaign.delivered_value,
            expected: campaign.bought_value,
            currencySign: getSymbolFromCurrency(campaign?.market?.currency?.code ?? ''),
        }),
        [
            campaign.bought_value,
            campaign.delivered_value,
            campaign.show_campaign_values_box,
            campaign?.market?.currency?.code,
        ],
    );
    const averageImpressions = useMemo(() => {
        const value =
            (pcaData as any)?.data?.campaign_stats?.dsp_average_impressions ??
            (pcaData as any)?.data?.campaign_stats?.dsp_average_impacts;
        return value == null ? null : Number(value);
    }, [pcaData]);

    const isMediaOwner = useMemo(() => chosenUserType === UserType.Owner, [chosenUserType]);
    const nonAuditedImpressionsPercentage = useMemo(() => {
        if (!isNonAuditedAudienceOnly) return null;

        const nonAuditedImpacts = Number((pcaData as any)?.data?.campaign_stats?.impacts ?? 0);
        const totalImpacts = Number(
            (nonAuditedTotalPcaData as any)?.data?.campaign_stats?.impacts ?? 0,
        );

        if (!totalImpacts) return null;

        return (nonAuditedImpacts / totalImpacts) * 100;
    }, [isNonAuditedAudienceOnly, nonAuditedTotalPcaData, pcaData]);

    const retrySummary = useCallback(() => reportWrapperRef.current?.refreshData(), []);

    const summaryContentLoading =
        summaryTotalsStale ||
        summaryLoadingState === NetworkRequestState.InProgress ||
        summaryFiltersStale ||
        (isDsp &&
            !isAverageAudienceMode &&
            !isNonAuditedAudienceOnly &&
            !hideImpressionAnalysis &&
            impactAnalysisLoading);

    useEffect(() => {
        if (loadedFieldFiltersKey != null && loadedFieldFiltersKey !== currentFieldFiltersKey) {
            reportWrapperRef?.current?.refreshData?.();
        }
    }, [currentFieldFiltersKey, loadedFieldFiltersKey]);
    const filterByLabel = useCommonTranslation('Modules.Main.Campaigns.Overview.Reports.filterBy');
    const hasSummaryFilters = useMemo(
        () => hasUserActiveFilters || hasSummaryDateFilter,
        [hasSummaryDateFilter, hasUserActiveFilters],
    );
    const isAllAudiencesSummary = useMemo(
        () =>
            isDsp &&
            !isAverageAudienceMode &&
            !isGeopathOnly &&
            dspAudienceFilterValues.length === 0,
        [dspAudienceFilterValues.length, isAverageAudienceMode, isDsp, isGeopathOnly],
    );
    /**
     * The overall summary only exists to give a filtered view something to
     * compare against. With no filters applied it asks for exactly what the main
     * summary already returned, so it is skipped and the effect below reuses the
     * main stats — one request instead of two, and no second set of numbers
     * arriving late to replace the first.
     */
    const shouldLoadOverallSummary = useMemo(
        () => isDsp && hasSummaryFilters,
        [isDsp, hasSummaryFilters],
    );
    const loadOverallUnfilteredSummary = useCallback(
        async (start: Date, end: Date, timeframe: ReportDateTimeframe | null) => {
            if (!shouldLoadOverallSummary) {
                overallSummaryRequestRef.current?.abort();
                latestOverallSummaryRequestKeyRef.current = null;

                // Only wipe the stats where there is no overall view to show. For an
                // unfiltered DSP campaign the main summary is the overall picture,
                // and the effect below owns that value — clearing it here would
                // blank the card and then repopulate it.
                if (!isDsp) {
                    setOverallUnfilteredCampaignStats(null);
                    setOverallUnfilteredSummaryDataDaysCount(0);
                }
                return;
            }

            const payload = {
                campaignId: id,
                startDate: undefined,
                endDate: undefined,
                // Scoped to the selected environment, as the map request is. Left
                // open it can fold sandbox in and disagree with the main summary.
                environment: environmentMode,
                fieldFilters: {},
                showStats: true,
                source,
            };
            const requestKey = JSON.stringify(payload);

            if (latestOverallSummaryRequestKeyRef.current === requestKey) return;

            latestOverallSummaryRequestKeyRef.current = requestKey;
            overallSummaryRequestRef.current?.abort();
            const requestData = apiClient.ReportsElasticSearch.summary(payload);
            overallSummaryRequestRef.current = requestData.controller;
            const [err, , response] = await dispatch(callApi(requestData.request));
            const nextData = !err && response?.data ? response.data : null;

            setOverallUnfilteredCampaignStats(
                nextData ? elasticSearchSummaryToOldSummary(nextData)?.campaignStats ?? null : null,
            );
            const totalDays = Math.max(
                differenceInCalendarDays(campaignTrueEnd, campaignTrueStart) + 1,
                1,
            );
            // Counted from this response's own days. It asks for a different window
            // to the summary on screen, so it cannot borrow that one's day count.
            const overallAsOfDate =
                latestDailyStatsDate((nextData as any)?.data?.daily_stats) ?? campaignAsOfDate;
            const elapsedDays = Math.min(
                Math.max(differenceInCalendarDays(overallAsOfDate, campaignTrueStart) + 1, 0),
                totalDays,
            );
            setOverallUnfilteredSummaryDataDaysCount(elapsedDays);
        },
        [
            campaignAsOfDate,
            campaignTrueEnd,
            campaignTrueStart,
            dispatch,
            environmentMode,
            id,
            isDsp,
            shouldLoadOverallSummary,
            source,
        ],
    );
    const onDataUpdated: ReportConfig<ElasticSearchSummaryResponse>['onDataUpdated'] = useCallback(
        async (reportData, start, end, $timeframe) => {
            if (!reportData) return;

            setPcaData(reportData);
            setLoadedFieldFiltersKey(currentFieldFiltersKeyRef.current);
            setSummaryTotalsStale(false);
            setTimeframe($timeframe);

            if ($timeframe) {
                latestSummaryWindow.current = {
                    start,
                    end,
                    timeframe: $timeframe,
                };
                fetchFilteredMapData(start, end, $timeframe);
                fetchOverallUnfilteredMapData(start, end, $timeframe);
                await loadOverallUnfilteredSummary(start, end, $timeframe);
                await loadCompanionSummaries({
                    start,
                    end,
                    timeframe: $timeframe,
                });
            } else {
                setImpactAnalysisLoading(false);
            }
        },
        [
            fetchFilteredMapData,
            fetchOverallUnfilteredMapData,
            loadCompanionSummaries,
            loadOverallUnfilteredSummary,
            setImpactAnalysisLoading,
        ],
    );
    const campaignDayProgress = useMemo(() => {
        const totalDays = Math.max(
            differenceInCalendarDays(campaignTrueEnd, campaignTrueStart) + 1,
            1,
        );
        const elapsedDays = Math.min(
            Math.max(differenceInCalendarDays(toDateAsOfDate, initialStartDate) + 1, 0),
            totalDays,
        );

        return {
            summaryDataDaysCount: elapsedDays,
            totalDays,
        };
    }, [toDateAsOfDate, campaignTrueEnd, campaignTrueStart, initialStartDate]);

    const overallCampaignDaysLive = useMemo(() => {
        const totalDays = Math.max(
            differenceInCalendarDays(campaignTrueEnd, campaignTrueStart) + 1,
            1,
        );
        return Math.min(
            Math.max(differenceInCalendarDays(campaignAsOfDate, campaignTrueStart) + 1, 0),
            totalDays,
        );
    }, [campaignAsOfDate, campaignTrueEnd, campaignTrueStart]);

    const toDateExpectedTarget = useMemo(() => {
        // UL-8133 -- vistar_us2: the target headline as given, never even pacing.
        if (ioTargets.isTargetCampaign) {
            return ioTargets.headline && ioTargets.headline !== 'hidden'
                ? ioTargets.headline.target ?? undefined
                : undefined;
        }
        if (!isDsp || campaign.expected_impacts == null) return adjustedCampaignTarget;
        const totalDays = Math.max(
            differenceInCalendarDays(campaignTrueEnd, campaignTrueStart) + 1,
            1,
        );
        const dailyExpected = (Number(campaign.expected_impacts) || 0) / totalDays;
        const daysForExpected = hasSummaryDateFilter
            ? campaignDayProgress.summaryDataDaysCount
            : overallCampaignDaysLive || campaignDayProgress.summaryDataDaysCount;
        return dailyExpected * daysForExpected;
    }, [
        isDsp,
        campaign.expected_impacts,
        campaignTrueEnd,
        campaignTrueStart,
        hasSummaryDateFilter,
        overallCampaignDaysLive,
        campaignDayProgress.summaryDataDaysCount,
        adjustedCampaignTarget,
        ioTargets.isTargetCampaign,
        ioTargets.headline,
    ]);
    const filteredSummaryEntityCount = useMemo(
        () => Object.keys(filteredMapData[currentFilteredMapDataKey]?.data ?? {}).length,
        [currentFilteredMapDataKey, filteredMapData],
    );
    const overallSummaryEntityCount = useMemo(
        () => Object.keys(overallUnfilteredMapData[currentFilteredMapDataKey]?.data ?? {}).length,
        [currentFilteredMapDataKey, overallUnfilteredMapData],
    );
    const filteredSummaryEntityLabel = useMemo(() => `${t('CommonWords.Frame')}s`, [t]);

    useEffect(() => {
        if (!isAllAudiencesSummary || hasSummaryFilters || !data?.campaignStats) return;

        setOverallUnfilteredCampaignStats(data.campaignStats);
        setOverallUnfilteredSummaryDataDaysCount(overallCampaignDaysLive);
    }, [overallCampaignDaysLive, data?.campaignStats, hasSummaryFilters, isAllAudiencesSummary]);

    if (resolvedType == null) return <Box />;

    return (
        <Box>
            <ReportWrapper
                onResetAdvancedFilters={onResetAdvancedFilters}
                ref={reportWrapperRef}
                fetchData={fetchData}
                onDataUpdated={onDataUpdated}
                onLoadingStateUpdated={(state?: NetworkRequest) => {
                    const nextState = state?.state ?? NetworkRequestState.Idle;
                    setSummaryLoadingState(nextState);
                    if (nextState === NetworkRequestState.InProgress) {
                        setSummaryTotalsStale(true);
                    } else {
                        setSummaryTotalsStale(false);
                    }
                }}
                dateSettings={dateSettings}
                initialStartDate={initialStartDate}
                initialEndDate={initialEndDate}
                reportChartWrapperProps={{ height: 500 }}
                reportWrapperProps={{ minHeight: 0 }}
                removeLoadingBar
                filterExtras={filterExtras}
                centerControls={centerControls}
                beforeChartComponent={
                    <Box py={1}>
                        <Collapse in={showAdvancedFilters.value}>
                            <Box pb={2}>{advancedFilter}</Box>
                        </Collapse>
                        {ioTargets.targetsHidden && (
                            <Alert severity="info" sx={{ mb: 1.5 }}>
                                {t(
                                    'Modules.Main.Campaigns.CampaignAnalytics.targetsHiddenForFilter',
                                )}
                            </Alert>
                        )}
                        {!summaryContentLoading && data?.campaignStats && (
                            <Box mt={1} minHeight={260} position="relative">
                                <PCATotalsByMetric
                                    stats={data.campaignStats}
                                    campaignTarget={toDateExpectedTarget}
                                    fullCampaignTarget={campaign.expected_impacts}
                                    targetHeadline={ioTargets.headline}
                                    auditedStats={
                                        auditedPcaData
                                            ? elasticSearchSummaryToOldSummary(auditedPcaData)
                                                  ?.campaignStats
                                            : null
                                    }
                                    playsTitle={
                                        resolvedType === 'classic'
                                            ? 'Modules.Main.Campaigns.Overview.Analytics.displayDaysTitle'
                                            : 'Modules.Main.Campaigns.Overview.Analytics.playsTitle'
                                    }
                                    averageImpressions={averageImpressions}
                                    showVariance={!isDsp}
                                    isGeopathOnly={hasUserActiveFilters}
                                    campaignValues={campaignValues}
                                    isMediaOwner={isMediaOwner}
                                    isDsp={isDsp}
                                    isAverageAudienceMode={isAverageAudienceMode}
                                    isNonAuditedMode={isNonAuditedAudienceOnly}
                                    hideImpressionAnalysis={
                                        hideImpressionAnalysis ||
                                        (!isAverageAudienceMode &&
                                            !isNonAuditedAudienceOnly &&
                                            impactAnalysisLoading)
                                    }
                                    nonAuditedImpressionsPercentage={
                                        nonAuditedImpressionsPercentage
                                    }
                                    varianceContextLabel={impressionAnalysisVarianceContextLabel}
                                    auditedVarianceLabel={impactAnalysisAudienceLabel}
                                    showAllAudiencesDeliveryOverview={isAllAudiencesSummary}
                                    hasActiveFilters={
                                        isAllAudiencesSummary
                                            ? hasUserActiveFilters
                                            : hasSummaryFilters
                                    }
                                    hasDateFilter={hasSummaryDateFilter}
                                    overallStats={overallUnfilteredCampaignStats}
                                    overallSummaryDataDaysCount={
                                        overallUnfilteredSummaryDataDaysCount
                                    }
                                    summaryDataDaysCount={campaignDayProgress.summaryDataDaysCount}
                                    totalCampaignDays={campaignDayProgress.totalDays}
                                    filteredEntityCount={filteredSummaryEntityCount}
                                    overallEntityCount={overallSummaryEntityCount}
                                    filteredEntityLabel={filteredSummaryEntityLabel}
                                />
                            </Box>
                        )}
                    </Box>
                }
                afterChartComponent={
                    <SummaryWidgetsBody
                        loading={summaryContentLoading}
                        failed={summaryLoadingState === NetworkRequestState.Error}
                        onRetry={retrySummary}
                    >
                        {renderedSummaryWidgets}
                    </SummaryWidgetsBody>
                }
                additionalDateControls={
                    <Box ml="auto" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isDsp && (
                            <>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<ExpandMoreIcon />}
                                    startIcon={<GroupsIcon />}
                                    onClick={e => setAnchorEl(e.currentTarget)}
                                    sx={{
                                        borderRadius: 999,
                                        textTransform: 'none',
                                        px: 2,
                                    }}
                                >
                                    {audienceButtonLabel}
                                </Button>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={() => setAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            onAdvancedFiltersUpdated(
                                                {
                                                    ...(selectedFiltersForUi ?? {}),
                                                    [DSP_AUDIENCE_FILTER_KEY]: [] as any,
                                                },
                                                countFields,
                                            );
                                            push(DSP_AUDIENCE_MODE_URL_KEY, null);
                                            setAnchorEl(null);
                                        }}
                                    >
                                        {t('Modules.Main.Campaigns.Overview.Analytics.audienceAll')}
                                    </MenuItem>

                                    {isAverageAudienceSupported && (
                                        <MenuItem
                                            onClick={() => {
                                                onAdvancedFiltersUpdated(
                                                    {
                                                        ...(selectedFiltersForUi ?? {}),
                                                        [DSP_AUDIENCE_FILTER_KEY]: [] as any,
                                                    },
                                                    countFields,
                                                );
                                                push(
                                                    DSP_AUDIENCE_MODE_URL_KEY,
                                                    DSP_AUDIENCE_MODE_AVERAGES,
                                                );
                                                setAnchorEl(null);
                                            }}
                                        >
                                            {t(
                                                'Modules.Main.Campaigns.Overview.Analytics.audienceAverages',
                                            )}
                                        </MenuItem>
                                    )}

                                    {audienceVersionOptions.hasNoAudience && (
                                        <MenuItem
                                            onClick={() => {
                                                onAdvancedFiltersUpdated(
                                                    {
                                                        ...(selectedFiltersForUi ?? {}),
                                                        [DSP_AUDIENCE_FILTER_KEY]: [
                                                            DSP_NO_AUDIENCE_VALUE,
                                                        ] as any,
                                                    },
                                                    countFields,
                                                );
                                                push(DSP_AUDIENCE_MODE_URL_KEY, null);
                                                setAnchorEl(null);
                                            }}
                                        >
                                            {t(
                                                'Modules.Main.Campaigns.Overview.Analytics.audienceNone',
                                            )}
                                        </MenuItem>
                                    )}

                                    {audienceVersionOptions.versions.map(v => (
                                        <MenuItem
                                            key={v}
                                            onClick={() => {
                                                onAdvancedFiltersUpdated(
                                                    {
                                                        ...(selectedFiltersForUi ?? {}),
                                                        [DSP_AUDIENCE_FILTER_KEY]: [v] as any,
                                                    },
                                                    countFields,
                                                );
                                                push(DSP_AUDIENCE_MODE_URL_KEY, null);
                                                setAnchorEl(null);
                                            }}
                                        >
                                            {v}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}

                        <Button
                            onClick={showAdvancedFilters?.toggle}
                            color={advancedFiltersCount > 0 ? 'primary' : 'secondary'}
                        >
                            <FilterList sx={{ mr: 0.5 }} />
                            {filterByLabel}
                            <KeyboardArrowDown />
                        </Button>
                    </Box>
                }
                additionalDateControlsPosition="end"
            />
        </Box>
    );
};

export default CampaignAnalyticsES;
